import {useCallback, useEffect, useReducer, ReactNode} from 'react';
import {useParams} from 'react-router-dom';
import moment from 'moment';
import {SeniorsSidebarContext} from './context';
import reducer from './reducer';
import {SeniorSidebarActionType, SeniorSidebarState} from './types';
import {UsersRepository} from '../../repositories/users';
import {Option} from '../../components/select-dropdown/select-dropdown';
import {User} from '../../backend/interfaces/user';
import {UserType} from '../../backend/enums/user-type';
import {AffiliatedUser} from '../../backend/responses/affiliated-user';
import {EntitySocialResponse} from '../../backend/interfaces/entity-social-response';

const initialState: SeniorSidebarState = {
  senior: undefined,
  list: undefined,
};

const LIMIT_PAGE_SIZE = 10;

export const SeniorSidebarContextProvider = ({children}: {children: ReactNode}) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  let {entityId, id}: {entityId: string; id: string} = useParams();

  const seniorAge = (birthday?: string): string => {
    if (!birthday) return '';
    return moment().diff(birthday, 'years').toString();
  };

  const seniorOptionLabel = useCallback((user: User): string => {
    return `${user.displayName || user.name}, ${seniorAge(user.birthday)}`;
  }, []);

  const loadEntitySeniorsList = useCallback(async () => {
    if (entityId === undefined) return;

    const seniors = new Map<
      string,
      Option & {originalData: User; socialResponses: EntitySocialResponse[]}
    >();

    let count = LIMIT_PAGE_SIZE;
    let skip = 0;

    while (count >= LIMIT_PAGE_SIZE) {
      const response = await UsersRepository.instance.getInEntity(
        entityId,
        LIMIT_PAGE_SIZE,
        skip,
        UserType.Senior,
        '',
        false
      );

      response.data.forEach((senior: AffiliatedUser) => {
        const id = senior.user.id || '';

        const option = {
          key: id,
          label: seniorOptionLabel(senior.user),
          picture: senior.user.picture,
          originalData: senior.user,
          socialResponses: senior.entitySocialResponses,
        };

        seniors.set(id, option);
      });

      count = response.data.length;
      skip += 10;
    }

    dispatch({
      type: SeniorSidebarActionType.SET_SENIOR_LIST,
      list: seniors,
    });
  }, [entityId, seniorOptionLabel]);

  const updateSelectedSenior = useCallback(() => {
    const senior = state.list?.get(id);

    if (senior) {
      dispatch({
        type: SeniorSidebarActionType.SET_SELECTED_SENIOR,
        senior,
      });
    }
  }, [id, state.list]);

  const updateSeniorData = (user: User, socialResponses?: EntitySocialResponse[]) => {
    if (user.id) {
      const updatedList = state.list?.set(user.id, {
        key: user.id,
        label: seniorOptionLabel(user),
        picture: user.picture,
        originalData: user,
        socialResponses: socialResponses
          ? socialResponses
          : state.list?.get(user.id)?.socialResponses || [],
      });

      if (updatedList) {
        dispatch({
          type: SeniorSidebarActionType.UPDATE_SENIOR_LIST,
          list: updatedList,
        });

        updateSelectedSenior();
      }
    }
  };

  useEffect(() => {
    if (state.list) updateSelectedSenior();
  }, [id, state.list, updateSelectedSenior]);

  useEffect(() => {
    loadEntitySeniorsList();
  }, [loadEntitySeniorsList]);

  return (
    <SeniorsSidebarContext.Provider
      value={{
        state,
        updateSeniorData,
      }}
    >
      {children}
    </SeniorsSidebarContext.Provider>
  );
};
